import { FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import { VehicleStatusCode } from "../../../api/types/VehicleStatusCode";
import ModalEventFilter from "../modalEventFilter";
import { StatusCodeFilter } from "../statusCodeFilter";

type Props = {
  // every event type present in what's being compared
  statusCodes: VehicleStatusCode[];
  // the current selection; null means pin everything, [] means pin nothing
  filteredStatusCodes: StatusCodeFilter;
  onChange: (codes: VehicleStatusCode[]) => void;
  // an account can rename its status codes
  statusCodeDescriptions?: Record<number, string>;
};

// The Event Filter button and modal for a compare view - the same control
// and the same modal the main maps use, shared by the asset map's compare
// view and the multi-vehicle one so the two can't drift apart.
//
// ModalEventFilter gates itself on one redux flag, so exactly one instance
// may be mounted at a time. The page-level ones are held back while a
// compare view is open (see `isComparing` at both call sites) and this takes
// over - otherwise the flag would open two stacked copies.
export const CompareEventFilter: FC<Props> = ({
  statusCodes,
  filteredStatusCodes,
  onChange,
  statusCodeDescriptions,
}) => {
  const dispatch = useDispatch();
  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );

  if (statusCodes.length === 0) return null;

  // an unapplied filter reads as all of them; an empty selection is a real
  // zero and has to show as one, or the count contradicts the map
  const activeCount = filteredStatusCodes
    ? filteredStatusCodes.length
    : statusCodes.length;
  const isNarrowed = activeCount < statusCodes.length;

  return (
    <>
      <button
        type="button"
        onClick={() =>
          dispatch(setModalsData({ ...modalsState, showEventFilter: true }))
        }
        className={`px-3 py-1.5 rounded-lg border text-sm font-medium hover:bg-accent-blue-paleO66 ${
          isNarrowed
            ? "border-accent-blue-dark text-accent-blue-dark bg-accent-blue-pale"
            : "border-accent-blue-bright text-accent-blue-bright"
        }`}
      >
        Event Filter
        <span className="ml-1 font-normal text-heading-gray888">
          ({activeCount}/{statusCodes.length})
        </span>
      </button>

      {modalsState?.showEventFilter && (
        <ModalEventFilter
          statusCodes={statusCodes}
          filteredStatusCodes={filteredStatusCodes}
          handleEventFiltersChange={onChange}
          statusCodeDescriptions={statusCodeDescriptions}
        />
      )}
    </>
  );
};
